/**
 * DEMO-ONLY: inbound sales quotes for Presidio Quoting (MVP 3.1 inbound model).
 * Quotes are delivered outside the site; customers view + add to cart once.
 * Removable with mock.interceptor.presidio quote hooks and mock-data fixtures.
 * PROVISIONAL: attributes pending ICM confirmation.
 */

import { formatQuotePdfFilename } from 'src/app/extensions/quoting/utils/presidio-quote-export';

/**
 * Customer-facing inbound quote statuses (4):
 * - Submitted — from integration; needs Approve or Reject
 * - Processing — MyPresidio after purchase; cannot purchase again
 * - Rejected — MyPresidio after reject; cannot add to cart
 * - Expired — past validToDate when not Processing or Rejected
 */
export type PresidioInboundQuoteStatus = 'Submitted' | 'Processing' | 'Expired' | 'Rejected';

export type PresidioQuoteLineType = 'Hardware' | 'Subscription' | 'Service';

export interface PresidioQuoteLineMeta {
  productSKU: string;
  lineNumber: number;
  manufacturer?: string;
  manufacturerPartNumber?: string;
  unspsc?: string;
  lineType?: PresidioQuoteLineType;
  recurringStart?: string;
  recurringEnd?: string;
  months?: number;
  name?: string;
  groupKey?: string;
  groupParent?: boolean;
  /** DEMO: unit price / qty for catalog-synthesized detail payloads. */
  unitPrice?: number;
  quantity?: number;
}

export interface PresidioQuoteAddressBlock {
  type: 'From' | 'To' | 'BillTo' | 'ShipTo';
  name?: string;
  company?: string;
  accountCode?: string;
  addressLines?: string[];
  cityStateZip?: string;
  phone?: string;
  email?: string;
  contactName?: string;
}

export interface PresidioQuoteWorkflowStep {
  name: string;
  role?: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  date?: string;
  notes?: string;
}

export type PresidioQuoteCategory = 'STANDARD' | 'PRES_FUNDS' | 'CARRIER';

export interface PresidioQuoteEnrichment {
  id: string;
  number: string;
  displayName: string;
  /** When false / PRES_FUNDS / CARRIER — filtered from customer list (3.1.5). */
  customerVisible: boolean;
  quoteCategory: PresidioQuoteCategory;
  creationDate: number;
  validFromDate: number;
  validToDate: number;
  itemCount: number;
  poNumber?: string;
  orderNumber?: string;
  /** DEMO: invoice created after quote purchase. */
  invoiceNumber?: string;
  pricingRequestNumber?: string;
  contact?: string;
  contractVehicle?: string;
  affiliatedCompany?: string;
  partNumbers?: string[];
  /** Session/checkout overlay may set true after purchase. */
  purchased?: boolean;
  sellerComment?: string;
  lineMeta?: PresidioQuoteLineMeta[];
  totalValue?: number;
  /** PDF-parity sales contacts (MVP 3.2.1). */
  accountManager?: string;
  insideSalesRep?: string;
  addresses?: PresidioQuoteAddressBlock[];
  attachments?: Array<{ name: string; url?: string }>;
  /**
   * Kept for session overlays; inbound quotes use workflow history for display (3.2.6).
   */
  pendingApproval?: boolean;
  rejected?: boolean;
  /** Customer rejection comments for MyOrders (MVP 3.4.1). */
  rejectionComment?: string;
  /** Optional customer attachment file name emailed to AM (FR13.7.7.1). */
  rejectionAttachmentName?: string;
  rejectedAt?: string;
  salesNotes?: string;
  /** Workflow History timeline (MVP 3.2.6) — display names/roles/timestamps. */
  approvalSteps?: PresidioQuoteWorkflowStep[];
  /**
   * Set when the customer generated the quote from cart/QO through the Quote integration.
   * Display status follows the 4-status model (Submitted until Processing / Rejected / Expired).
   */
  submittedDate?: number;
}

export const PRESIDIO_MOCK_QUOTE_OVERLAYS_KEY = 'presidio-mock-quote-overlays';
/** @deprecated Use PRESIDIO_MOCK_QUOTE_OVERLAYS_KEY */
export const PRESIDIO_MOCK_QUOTE_APPROVALS_KEY = PRESIDIO_MOCK_QUOTE_OVERLAYS_KEY;

export const PRESIDIO_MOCK_ACTIVE_QUOTE_BASKET_KEY = 'presidio-mock-active-quote-basket';

/** DEMO-ONLY: outbound quote rejections for MyOrders inspectability (PROVISIONAL). */
export const PRESIDIO_MOCK_MYORDERS_REJECTIONS_KEY = 'presidio.mock.myorders.rejections';

/**
 * DEMO-ONLY: quote-rejection emails to Account Manager (FR13.7.7 / FR21.3.6).
 * Inspectable sink — not a real mail send.
 */
export const PRESIDIO_MOCK_QUOTE_REJECTION_AM_EMAILS_KEY = 'presidio.mock.quote.rejection.am.emails';

/** DEMO-ONLY: quotes created via portal Generate Quote → MyOrders (MVP 2.1.11). */
export const PRESIDIO_MOCK_PORTAL_QUOTES_KEY = 'presidio.mock.portal.quotes';

/** DEMO inspectability: last MyOrders Generate Quote submits (PROVISIONAL). */
export const PRESIDIO_MOCK_MYORDERS_QUOTE_SUBMITS_KEY = 'presidio.mock.myorders.quote_submits';

export interface PresidioMockMyOrdersRejection {
  quoteId: string;
  rejectionComment: string;
  rejectedAt: string;
  accountManager?: string;
  accountManagerEmail?: string;
  /** Optional customer file name emailed to the AM (FR13.7.7.1). */
  attachmentName?: string;
}

/** DEMO: recorded AM notification when a quote is rejected (PROVISIONAL). */
export interface PresidioMockQuoteRejectionAmEmail {
  quoteId: string;
  rejectionComment: string;
  rejectedAt: string;
  accountManager: string;
  accountManagerEmail: string;
  attachmentName?: string;
}

export interface PresidioPortalQuoteSubmitRequest {
  source: 'cart' | 'quick_order';
  accountName?: string;
  billTo: PresidioQuoteAddressBlock;
  shipTo: PresidioQuoteAddressBlock;
  /** Extra unique ship-tos when the quote uses split shipping. */
  additionalShipTos?: PresidioQuoteAddressBlock[];
  lines: Array<{
    productSKU: string;
    partNumber: string;
    description: string;
    quantity: number;
    unitPrice?: number;
    extPrice?: number;
    manufacturer?: string;
  }>;
  subtotal?: number;
  currency?: string;
}

export interface PresidioPortalQuoteSubmitResult {
  quoteId: string;
  quoteNumber: string;
  enrichment: PresidioQuoteEnrichment;
}

type QuoteOverlay = {
  purchased?: boolean;
  poNumber?: string;
  orderNumber?: string;
  rejected?: boolean;
  rejectionComment?: string;
  rejectionAttachmentName?: string;
  rejectedAt?: string;
};

/** Active New quotes expire end of 2026 (demo — every inbound quote has an expiration). */
const ACTIVE_VALID_TO = Date.parse('2026-12-31T23:59:59.000Z');

/** DEMO: PDF-parity address / sales contacts for Acme quotes (MVP 3.2.1). */
const ACME_QUOTE_ADDRESSES: PresidioQuoteAddressBlock[] = [
  {
    type: 'From',
    name: 'Riley Sales',
    company: 'Presidio Networked Solutions LLC',
    addressLines: ['100 Presidio Way', 'Suite 400'],
    cityStateZip: 'Austin, TX 78701',
    phone: '512.555.0100',
    email: 'riley.sales@presidio.com',
  },
  {
    type: 'To',
    company: 'Acme Healthcare',
    accountCode: 'ACME001',
    name: 'Alex Rivera',
    phone: '(512) 555-2000 Ext. 0000',
  },
  {
    type: 'BillTo',
    company: 'Acme Healthcare',
    accountCode: 'ACME001',
    addressLines: ['100 Healthcare Way', 'Suite 400'],
    cityStateZip: 'Austin, TX 78701',
    contactName: 'Jordan Hale',
    email: 'jordan.hale@acme.com',
    phone: '512-555-0100',
  },
  {
    type: 'ShipTo',
    company: 'Acme Healthcare',
    addressLines: ['250 Distribution Blvd'],
    cityStateZip: 'Round Rock, TX 78664',
    contactName: 'Alex Rivera',
    phone: '(512) 555-2000 Ext. 0000',
  },
];

/** Fully completed approval path (demo audit history). */
const ACME_QUOTE_WORKFLOW_APPROVED: PresidioQuoteWorkflowStep[] = [
  {
    name: 'Riley Sales',
    role: 'Presidio Sales Team',
    status: 'Approved',
    date: '2026-03-01T12:33:11.000Z',
  },
  {
    name: 'Morgan Chen',
    role: 'Quote Approver Level I',
    status: 'Approved',
    date: '2026-03-01T12:35:36.000Z',
  },
  {
    name: 'Jordan Hale',
    role: 'Quote Approver',
    status: 'Approved',
    date: '2026-03-01T12:37:19.000Z',
  },
  {
    name: 'Riley Sales',
    role: 'Presidio Sales Team',
    status: 'Approved',
    date: '2026-03-01T12:37:19.000Z',
  },
];

/** In-progress path — next approver still Pending. */
const ACME_QUOTE_WORKFLOW_PENDING: PresidioQuoteWorkflowStep[] = [
  {
    name: 'Riley Sales',
    role: 'Presidio Sales Team',
    status: 'Approved',
    date: '2026-04-02T09:12:04.000Z',
  },
  {
    name: 'Morgan Chen',
    role: 'Quote Approver Level I',
    status: 'Approved',
    date: '2026-04-02T10:05:22.000Z',
    notes: 'Looks good — forwarding for final approval.',
  },
  {
    name: 'Jordan Hale',
    role: 'Quote Approver',
    status: 'Pending',
  },
];

/** Rejected mid-workflow. */
const ACME_QUOTE_WORKFLOW_REJECTED: PresidioQuoteWorkflowStep[] = [
  {
    name: 'Riley Sales',
    role: 'Presidio Sales Team',
    status: 'Approved',
    date: '2025-02-10T14:20:00.000Z',
  },
  {
    name: 'Morgan Chen',
    role: 'Quote Approver Level I',
    status: 'Approved',
    date: '2025-02-10T15:01:44.000Z',
  },
  {
    name: 'Jordan Hale',
    role: 'Quote Approver',
    status: 'Rejected',
    date: '2025-02-11T09:18:03.000Z',
    notes: 'Budget not approved for this FY — please resubmit next quarter.',
  },
];

/** Shorter two-step approval. */
const ACME_QUOTE_WORKFLOW_SHORT: PresidioQuoteWorkflowStep[] = [
  {
    name: 'Riley Sales',
    role: 'Presidio Sales Team',
    status: 'Approved',
    date: '2026-05-12T11:00:00.000Z',
  },
  {
    name: 'Sam Patel',
    role: 'Account Owner',
    status: 'Approved',
    date: '2026-05-12T11:22:41.000Z',
  },
];

/** @deprecated alias — prefer ACME_QUOTE_WORKFLOW_APPROVED */
const ACME_QUOTE_WORKFLOW = ACME_QUOTE_WORKFLOW_APPROVED;

/** DEMO: generic sales attachments on quotes (not invoice documents). */
function quoteAttachments(
  ...names: string[]
): Array<{ name: string }> {
  return names.map(name => ({ name }));
}

/** Seed catalog — inbound from sales; also mirrored into mock-data list/detail JSON. */
export const PRESIDIO_QUOTE_CATALOG: Record<string, PresidioQuoteEnrichment> = {
  'MOCK-Q-001': {
    id: 'MOCK-Q-001',
    number: 'Q-2001',
    displayName: 'DGX H100 cluster — Acme Q4',
    customerVisible: true,
    quoteCategory: 'STANDARD',
    creationDate: Date.parse('2026-03-01T12:00:00.000Z'),
    validFromDate: Date.parse('2026-03-01T12:00:00.000Z'),
    validToDate: ACTIVE_VALID_TO,
    itemCount: 1,
    pricingRequestNumber: 'PR-2201',
    invoiceNumber: 'INV-9001',
    contact: 'Alex Rivera',
    contractVehicle: 'SEWP V',
    affiliatedCompany: 'Acme Healthcare',
    accountManager: 'Eric Mohr',
    insideSalesRep: 'Riley Sales',
    addresses: ACME_QUOTE_ADDRESSES,
    attachments: quoteAttachments(formatQuotePdfFilename('Q-2001'), 'DGX-H100-configuration-notes.docx', 'Site-requirements.pdf'),
    approvalSteps: ACME_QUOTE_WORKFLOW,
    salesNotes: 'Please review and Approve to add to cart, or Reject with comments for sales.',
    partNumbers: ['NV-DGX-H100'],
    sellerComment: 'Volume pricing applied for Acme Healthcare.',
    lineMeta: [
      {
        productSKU: 'NV-DGX-H100',
        lineNumber: 1,
        manufacturer: 'NVIDIA',
        manufacturerPartNumber: 'NV-DGX-H100',
        unspsc: '43.21.15.08',
        lineType: 'Hardware',
        name: 'DGX H100 System',
      },
    ],
    totalValue: 379000,
  },
  'MOCK-Q-002': {
    id: 'MOCK-Q-002',
    number: 'Q-2002',
    displayName: 'AI lab refresh — GPU + support',
    customerVisible: true,
    quoteCategory: 'STANDARD',
    creationDate: Date.parse('2026-03-08T12:00:00.000Z'),
    validFromDate: Date.parse('2026-03-08T12:00:00.000Z'),
    validToDate: ACTIVE_VALID_TO,
    itemCount: 2,
    pricingRequestNumber: 'PR-2200',
    invoiceNumber: 'INV-9002',
    contact: 'Alex Rivera',
    contractVehicle: 'Cisco EA',
    affiliatedCompany: 'Acme Healthcare',
    accountManager: 'Vincent F Trama',
    insideSalesRep: 'Riley Sales',
    addresses: ACME_QUOTE_ADDRESSES,
    attachments: quoteAttachments(formatQuotePdfFilename('Q-2002'), 'GPU-lab-BOM.xlsx', 'Support-coverage-summary.pdf'),
    approvalSteps: ACME_QUOTE_WORKFLOW.map((step, i) =>
      i === 0 ? { ...step, date: '2026-03-08T14:10:00.000Z' } : { ...step, date: '2026-03-08T14:15:00.000Z' }
    ),
    partNumbers: ['NV-L40S-48G', 'SUPPORT-3YR'],
    sellerComment: 'Quote prepared by your Presidio sales team.',
    lineMeta: [
      {
        productSKU: 'NV-L40S-48G',
        lineNumber: 1,
        manufacturer: 'NVIDIA',
        manufacturerPartNumber: 'NV-L40S-48G',
        unspsc: '43.21.15.08',
        lineType: 'Hardware',
        name: 'L40S GPU',
        groupKey: 'gpu-bundle',
        groupParent: true,
      },
      {
        productSKU: 'SUPPORT-3YR',
        lineNumber: 2,
        manufacturer: 'Presidio',
        manufacturerPartNumber: 'SUPPORT-3YR',
        unspsc: '81.11.18.00',
        lineType: 'Subscription',
        name: 'Support subscription',
        recurringStart: '2026-10-01',
        recurringEnd: '2029-09-30',
        months: 36,
        groupKey: 'gpu-bundle',
      },
    ],
    totalValue: 48500,
  },
  'MOCK-Q-003': {
    id: 'MOCK-Q-003',
    number: 'Q-2003',
    displayName: 'Edge starter kit',
    customerVisible: true,
    quoteCategory: 'STANDARD',
    creationDate: Date.parse('2025-06-01T12:00:00.000Z'),
    validFromDate: Date.parse('2025-06-01T12:00:00.000Z'),
    validToDate: Date.parse('2026-09-30T23:59:59.000Z'),
    itemCount: 1,
    poNumber: 'PO-88421',
    orderNumber: 'ORD-55102',
    invoiceNumber: 'INV-9006',
    contact: 'Alex Rivera',
    contractVehicle: 'SEWP V',
    affiliatedCompany: 'Acme Healthcare',
    accountManager: 'Eric Mohr',
    insideSalesRep: 'Riley Sales',
    addresses: ACME_QUOTE_ADDRESSES,
    attachments: quoteAttachments(formatQuotePdfFilename('Q-2003'), 'Edge-kit-BOM.pdf', 'Deployment-checklist.docx'),
    approvalSteps: ACME_QUOTE_WORKFLOW,
    partNumbers: ['PRS-EDGE-KIT'],
    purchased: true,
    sellerComment: 'Converted to order ORD-55102 / invoice INV-9006.',
    lineMeta: [
      {
        productSKU: 'PRS-EDGE-KIT',
        lineNumber: 1,
        manufacturer: 'Presidio',
        manufacturerPartNumber: 'PRS-EDGE-KIT',
        lineType: 'Hardware',
        name: 'Edge starter kit',
      },
    ],
    totalValue: 12500,
  },
  'MOCK-Q-004': {
    id: 'MOCK-Q-004',
    number: 'Q-1990',
    displayName: 'Storage expansion',
    customerVisible: true,
    quoteCategory: 'STANDARD',
    creationDate: Date.parse('2024-01-01T12:00:00.000Z'),
    validFromDate: Date.parse('2024-01-01T12:00:00.000Z'),
    validToDate: Date.parse('2024-04-01T12:00:00.000Z'),
    itemCount: 1,
    contact: 'Alex Rivera',
    affiliatedCompany: 'Acme Healthcare',
    accountManager: 'Vincent F Trama',
    insideSalesRep: 'Riley Sales',
    addresses: ACME_QUOTE_ADDRESSES.slice(0, 2),
    attachments: quoteAttachments(formatQuotePdfFilename('Q-1990'), 'Storage-sizing-worksheet.xlsx'),
    approvalSteps: [
      {
        name: 'Riley Sales',
        role: 'Presidio Sales Team',
        status: 'Approved',
        date: '2024-01-02T16:00:00.000Z',
      },
      {
        name: 'Morgan Chen',
        role: 'Quote Approver Level I',
        status: 'Approved',
        date: '2024-01-03T09:40:12.000Z',
      },
      {
        name: 'Jordan Hale',
        role: 'Quote Approver',
        status: 'Approved',
        date: '2024-01-03T11:15:00.000Z',
      },
    ],
    partNumbers: ['DELL-AI-STOR-60'],
    sellerComment: 'This quote has expired. Contact sales for a new quote.',
    lineMeta: [
      {
        productSKU: 'DELL-AI-STOR-60',
        lineNumber: 1,
        manufacturer: 'Dell',
        manufacturerPartNumber: 'DELL-AI-STOR-60',
        lineType: 'Hardware',
        name: 'AI Storage 60TB',
        unitPrice: 89000,
        quantity: 1,
      },
    ],
    totalValue: 89000,
  },
  /** Rejected — status Rejected; cannot add to cart. */
  'MOCK-Q-005': {
    id: 'MOCK-Q-005',
    number: 'Q-1988',
    displayName: 'Networking refresh',
    customerVisible: true,
    quoteCategory: 'STANDARD',
    creationDate: Date.parse('2025-02-01T12:00:00.000Z'),
    validFromDate: Date.parse('2025-02-01T12:00:00.000Z'),
    validToDate: Date.parse('2025-03-15T23:59:59.000Z'),
    itemCount: 1,
    contact: 'Alex Rivera',
    contractVehicle: 'Cisco EA',
    affiliatedCompany: 'Acme Healthcare',
    accountManager: 'Eric Mohr',
    insideSalesRep: 'Riley Sales',
    addresses: ACME_QUOTE_ADDRESSES,
    attachments: quoteAttachments(formatQuotePdfFilename('Q-1988'), 'Networking-design-notes.docx'),
    rejected: true,
    approvalSteps: ACME_QUOTE_WORKFLOW_REJECTED,
    partNumbers: ['CISCO-C9300'],
    sellerComment: 'Customer declined — see workflow notes.',
    lineMeta: [
      {
        productSKU: 'CISCO-C9300',
        lineNumber: 1,
        manufacturer: 'Cisco',
        manufacturerPartNumber: 'CISCO-C9300',
        unspsc: '43.22.26.07',
        lineType: 'Hardware',
        name: 'Catalyst 9300 48-port Switch',
        unitPrice: 22000,
        quantity: 1,
      },
    ],
    totalValue: 22000,
  },
  /** In-progress approval — next step Pending (demo audit history). */
  'MOCK-Q-006': {
    id: 'MOCK-Q-006',
    number: 'Q-2010',
    displayName: 'Campus Wi‑Fi upgrade',
    customerVisible: true,
    quoteCategory: 'STANDARD',
    creationDate: Date.parse('2026-04-02T08:00:00.000Z'),
    validFromDate: Date.parse('2026-04-02T08:00:00.000Z'),
    validToDate: ACTIVE_VALID_TO,
    itemCount: 2,
    pricingRequestNumber: 'PR-2310',
    contact: 'Alex Rivera',
    contractVehicle: 'SEWP V',
    affiliatedCompany: 'Acme Healthcare',
    accountManager: 'Vincent F Trama',
    insideSalesRep: 'Riley Sales',
    addresses: ACME_QUOTE_ADDRESSES,
    attachments: quoteAttachments(formatQuotePdfFilename('Q-2010'), 'WiFi-site-survey.pdf', 'Heatmap-summary.pdf'),
    approvalSteps: ACME_QUOTE_WORKFLOW_PENDING,
    partNumbers: ['CISCO-C9300', 'SUPPORT-3YR'],
    sellerComment: 'Waiting on final customer approver (Jordan Hale).',
    lineMeta: [
      {
        productSKU: 'CISCO-C9300',
        lineNumber: 1,
        manufacturer: 'Cisco',
        manufacturerPartNumber: 'CISCO-C9300',
        unspsc: '43.22.26.07',
        lineType: 'Hardware',
        name: 'Catalyst 9300 48-port Switch',
        unitPrice: 18500,
        quantity: 4,
        groupKey: 'wifi-bundle',
        groupParent: true,
      },
      {
        productSKU: 'SUPPORT-3YR',
        lineNumber: 2,
        manufacturer: 'Presidio',
        manufacturerPartNumber: 'SUPPORT-3YR',
        unspsc: '81.11.18.00',
        lineType: 'Subscription',
        name: 'SmartNet / support (3 yr)',
        unitPrice: 9600,
        quantity: 1,
        months: 36,
        groupKey: 'wifi-bundle',
      },
    ],
    totalValue: 83600,
  },
  /** Short completed workflow — New / ready to purchase. */
  'MOCK-Q-007': {
    id: 'MOCK-Q-007',
    number: 'Q-2011',
    displayName: 'Endpoint security bundle',
    customerVisible: true,
    quoteCategory: 'STANDARD',
    creationDate: Date.parse('2026-05-12T10:30:00.000Z'),
    validFromDate: Date.parse('2026-05-12T10:30:00.000Z'),
    validToDate: ACTIVE_VALID_TO,
    itemCount: 1,
    contact: 'Alex Rivera',
    contractVehicle: 'Cisco EA',
    affiliatedCompany: 'Acme Healthcare',
    accountManager: 'Eric Mohr',
    insideSalesRep: 'Riley Sales',
    addresses: ACME_QUOTE_ADDRESSES,
    attachments: [],
    approvalSteps: ACME_QUOTE_WORKFLOW_SHORT,
    partNumbers: ['NV-L40S-48G'],
    sellerComment: 'Fast-track approval completed by account owner.',
    lineMeta: [
      {
        productSKU: 'NV-L40S-48G',
        lineNumber: 1,
        manufacturer: 'NVIDIA',
        manufacturerPartNumber: 'NV-L40S-48G',
        unspsc: '43.21.15.08',
        lineType: 'Hardware',
        name: 'L40S GPU',
        unitPrice: 27500,
        quantity: 2,
      },
    ],
    totalValue: 55000,
  },
  /** Hidden from customer list (3.1.5) — PRES Funds */
  'MOCK-Q-HIDDEN-PRES': {
    id: 'MOCK-Q-HIDDEN-PRES',
    number: 'Q-PRES-01',
    displayName: 'PRES Funds internal',
    customerVisible: false,
    quoteCategory: 'PRES_FUNDS',
    creationDate: Date.parse('2026-03-01T12:00:00.000Z'),
    validFromDate: Date.parse('2026-03-01T12:00:00.000Z'),
    validToDate: ACTIVE_VALID_TO,
    itemCount: 1,
    partNumbers: ['NV-DGX-H100'],
    totalValue: 1,
  },
  /** Hidden — Carrier */
  'MOCK-Q-HIDDEN-CARRIER': {
    id: 'MOCK-Q-HIDDEN-CARRIER',
    number: 'Q-CAR-01',
    displayName: 'Carrier quote',
    customerVisible: false,
    quoteCategory: 'CARRIER',
    creationDate: Date.parse('2026-03-01T12:00:00.000Z'),
    validFromDate: Date.parse('2026-03-01T12:00:00.000Z'),
    validToDate: ACTIVE_VALID_TO,
    itemCount: 1,
    partNumbers: ['NV-DGX-H100'],
    totalValue: 1,
  },
};

export function isPresidioQuoteCustomerVisible(enrichment: PresidioQuoteEnrichment | undefined): boolean {
  if (!enrichment) {
    return true;
  }
  if (!enrichment.customerVisible) {
    return false;
  }
  return enrichment.quoteCategory === 'STANDARD';
}

function readOverlays(): Record<string, QuoteOverlay> {
  if (typeof sessionStorage === 'undefined') {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_QUOTE_OVERLAYS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, QuoteOverlay>) : {};
  } catch {
    return {};
  }
}

function writeOverlays(map: Record<string, QuoteOverlay>): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_QUOTE_OVERLAYS_KEY, JSON.stringify(map));
}

function readPortalQuotes(): Record<string, PresidioQuoteEnrichment> {
  if (typeof sessionStorage === 'undefined') {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_PORTAL_QUOTES_KEY);
    return raw ? (JSON.parse(raw) as Record<string, PresidioQuoteEnrichment>) : {};
  } catch {
    return {};
  }
}

function writePortalQuotes(map: Record<string, PresidioQuoteEnrichment>): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_PORTAL_QUOTES_KEY, JSON.stringify(map));
}

export function getPresidioQuoteEnrichment(quoteId: string): PresidioQuoteEnrichment | undefined {
  const portal = readPortalQuotes()[quoteId];
  if (portal) {
    const overlay = readOverlays()[quoteId];
    if (!overlay) {
      return { ...portal };
    }
    return {
      ...portal,
      purchased: overlay.purchased ?? portal.purchased,
      poNumber: overlay.poNumber ?? portal.poNumber,
      orderNumber: overlay.orderNumber ?? portal.orderNumber,
      rejected: overlay.rejected ?? portal.rejected,
      rejectionComment: overlay.rejectionComment ?? portal.rejectionComment,
      rejectionAttachmentName: overlay.rejectionAttachmentName ?? portal.rejectionAttachmentName,
      rejectedAt: overlay.rejectedAt ?? portal.rejectedAt,
    };
  }

  const base = PRESIDIO_QUOTE_CATALOG[quoteId];
  if (!base) {
    return undefined;
  }
  const overlay = readOverlays()[quoteId];
  if (!overlay) {
    return { ...base };
  }
  return {
    ...base,
    purchased: overlay.purchased ?? base.purchased,
    poNumber: overlay.poNumber ?? base.poNumber,
    orderNumber: overlay.orderNumber ?? base.orderNumber,
    pendingApproval: false,
    rejected: overlay.rejected ?? base.rejected,
    rejectionComment: overlay.rejectionComment ?? base.rejectionComment,
    rejectionAttachmentName: overlay.rejectionAttachmentName ?? base.rejectionAttachmentName,
    rejectedAt: overlay.rejectedAt ?? base.rejectedAt,
    approvalSteps: base.approvalSteps,
  };
}

/** Resolve inbound display status (Rejected / Processing win over Expired / Submitted). */
export function resolvePresidioInboundQuoteStatus(
  enrichment: PresidioQuoteEnrichment | undefined,
  now = Date.now()
): PresidioInboundQuoteStatus {
  if (!enrichment) {
    return 'Submitted';
  }
  if (enrichment.rejected) {
    return 'Rejected';
  }
  if (enrichment.purchased) {
    return 'Processing';
  }
  if (enrichment.validToDate < now) {
    return 'Expired';
  }
  return 'Submitted';
}

/**
 * True when this quote id is currently on the demo basket (blocks a second Add to cart).
 * Prefers session basket lines; clears a stale active-quote key when no lines remain.
 * (Reads `presidio-mock-basket-lines` directly to avoid a mock circular import.)
 */
export function isPresidioQuoteOnActiveBasket(quoteId: string | undefined): boolean {
  if (!quoteId || typeof sessionStorage === 'undefined') {
    return false;
  }
  try {
    const raw = sessionStorage.getItem('presidio-mock-basket-lines');
    const lines = raw ? (JSON.parse(raw) as Array<{ quoteId?: string }>) : [];
    if (Array.isArray(lines) && lines.some(line => line.quoteId === quoteId)) {
      return true;
    }
  } catch {
    // ignore corrupt session payload
  }
  if (getPresidioActiveQuoteOnBasket() === quoteId) {
    clearPresidioActiveQuoteOnBasket();
  }
  return false;
}

export function canAddPresidioQuoteToCart(enrichment: PresidioQuoteEnrichment | undefined): boolean {
  if (resolvePresidioInboundQuoteStatus(enrichment) !== 'Submitted') {
    return false;
  }
  // Same quote cannot be added twice to the current cart.
  return !isPresidioQuoteOnActiveBasket(enrichment?.id);
}

export function listVisiblePresidioQuoteIds(): string[] {
  const catalogIds = Object.keys(PRESIDIO_QUOTE_CATALOG);
  const portalIds = Object.keys(readPortalQuotes());
  return [...portalIds, ...catalogIds].filter(id =>
    isPresidioQuoteCustomerVisible(getPresidioQuoteEnrichment(id))
  );
}

/** Quote # format for portal → MyOrders (e.g. Q-261026…). */
export function nextPresidioPortalQuoteNumber(): string {
  const stamp = new Date();
  const yy = String(stamp.getFullYear()).slice(-2);
  const mm = String(stamp.getMonth() + 1).padStart(2, '0');
  const dd = String(stamp.getDate()).padStart(2, '0');
  const seq = String(
    stamp.getHours() * 10000 + stamp.getMinutes() * 100 + stamp.getSeconds() + (stamp.getMilliseconds() % 100)
  ).padStart(6, '0');
  return `Q-${yy}${mm}${dd}${seq}`.slice(0, 14);
}

/**
 * DEMO MyOrders: accept Generate Quote payload, assign quote #, persist for Quotes list.
 * Production: POST to MyOrders; ICM mirrors the resulting inbound quote (PROVISIONAL).
 */
export function submitPresidioPortalQuoteToMyOrders(
  request: PresidioPortalQuoteSubmitRequest
): PresidioPortalQuoteSubmitResult {
  const quoteNumber = nextPresidioPortalQuoteNumber();
  const quoteId = `PORTAL-${quoteNumber}`;
  const now = Date.now();
  const validTo = Date.parse(`${new Date().getFullYear()}-12-31T23:59:59.000Z`);

  const lineMeta: PresidioQuoteLineMeta[] = request.lines.map((line, idx) => ({
    productSKU: line.productSKU,
    lineNumber: idx + 1,
    manufacturer: line.manufacturer,
    manufacturerPartNumber: line.partNumber,
    name: line.description,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
  }));

  const enrichment: PresidioQuoteEnrichment = {
    id: quoteId,
    number: quoteNumber,
    displayName:
      request.source === 'quick_order'
        ? `Portal quote — Quick Order (${quoteNumber})`
        : `Portal quote — Cart (${quoteNumber})`,
    customerVisible: true,
    quoteCategory: 'STANDARD',
    creationDate: now,
    validFromDate: now,
    validToDate: validTo,
    submittedDate: now,
    itemCount: request.lines.length,
    affiliatedCompany: request.accountName,
    contact: request.billTo.contactName || request.billTo.name,
    sellerComment: 'Generated from MyPresidio Generate Quote and submitted to MyOrders (demo).',
    salesNotes: request.additionalShipTos?.length
      ? `Customer-confirmed bill-to and ${1 + request.additionalShipTos.length} ship-to addresses (split shipping). Awaiting sales review.`
      : 'Customer-confirmed bill-to and ship-to addresses. Awaiting sales review.',
    addresses: [
      {
        type: 'From',
        name: 'Presidio Sales',
        company: 'Presidio Networked Solutions LLC',
        addressLines: ['100 Presidio Way', 'Suite 400'],
        cityStateZip: 'Austin, TX 78701',
        email: 'quotes@presidio.com',
      },
      {
        type: 'To',
        company: request.accountName || request.billTo.company,
        name: request.billTo.contactName || request.billTo.name,
      },
      { ...request.billTo, type: 'BillTo' },
      { ...request.shipTo, type: 'ShipTo' },
      ...(request.additionalShipTos || []).map(addr => ({ ...addr, type: 'ShipTo' as const })),
    ],
    partNumbers: request.lines.map(l => l.partNumber).filter(Boolean),
    lineMeta,
    totalValue: request.subtotal,
    attachments: [{ name: formatQuotePdfFilename(quoteNumber) }],
  };

  const map = readPortalQuotes();
  map[quoteId] = enrichment;
  writePortalQuotes(map);

  if (typeof sessionStorage !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(PRESIDIO_MOCK_MYORDERS_QUOTE_SUBMITS_KEY);
      const list = raw ? (JSON.parse(raw) as unknown[]) : [];
      const next = Array.isArray(list) ? list : [];
      next.unshift({
        submittedAt: new Date().toISOString(),
        quoteId,
        quoteNumber,
        source: request.source,
        accountName: request.accountName,
        billTo: request.billTo,
        shipTo: request.shipTo,
        additionalShipTos: request.additionalShipTos,
        shipToCount: 1 + (request.additionalShipTos?.length || 0),
        lineCount: request.lines.length,
        subtotal: request.subtotal,
        currency: request.currency,
      });
      sessionStorage.setItem(PRESIDIO_MOCK_MYORDERS_QUOTE_SUBMITS_KEY, JSON.stringify(next.slice(0, 20)));
    } catch {
      // ignore
    }
  }

  return { quoteId, quoteNumber, enrichment };
}

/** Count customer-visible Submitted quotes (need Approve / Reject) for Approvals badge. */
export function countPresidioQuotesPendingApproval(): number {
  return listVisiblePresidioQuoteIds().filter(
    id => resolvePresidioInboundQuoteStatus(getPresidioQuoteEnrichment(id)) === 'Submitted'
  ).length;
}

/**
 * DEMO: remember which inbound quote was added to the basket so checkout can mark Processing.
 */
export function setPresidioActiveQuoteOnBasket(quoteId: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_ACTIVE_QUOTE_BASKET_KEY, quoteId);
}

export function getPresidioActiveQuoteOnBasket(): string {
  if (typeof sessionStorage === 'undefined') {
    return '';
  }
  return (sessionStorage.getItem(PRESIDIO_MOCK_ACTIVE_QUOTE_BASKET_KEY) || '').trim();
}

export function clearPresidioActiveQuoteOnBasket(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(PRESIDIO_MOCK_ACTIVE_QUOTE_BASKET_KEY);
  }
}

/**
 * Mark quote Processing after checkout (blocks re-add to cart).
 * Persists via `purchased` overlay flag for ICM attribute compatibility.
 */
export function markPresidioQuotePurchased(
  quoteId: string,
  opts?: { poNumber?: string; orderNumber?: string }
): PresidioQuoteEnrichment | undefined {
  const current = getPresidioQuoteEnrichment(quoteId);
  if (!current || !isPresidioQuoteCustomerVisible(current)) {
    return current;
  }
  const overlays = readOverlays();
  overlays[quoteId] = {
    ...overlays[quoteId],
    purchased: true,
    poNumber: opts?.poNumber || overlays[quoteId]?.poNumber || current.poNumber,
    orderNumber: opts?.orderNumber || overlays[quoteId]?.orderNumber || current.orderNumber,
  };
  writeOverlays(overlays);
  clearPresidioActiveQuoteOnBasket();
  return getPresidioQuoteEnrichment(quoteId);
}

/** If the basket still has an active quote id, mark it Processing on order create. */
export function markActivePresidioQuotePurchasedOnCheckout(orderNumber?: string): void {
  const quoteId = getPresidioActiveQuoteOnBasket();
  if (!quoteId) {
    return;
  }
  markPresidioQuotePurchased(quoteId, { orderNumber });
}

/**
 * Approve is ATC-only in the sales-sent path (MVP 3.4.1 / decision 1A) — no status change.
 * Kept for call-site compatibility.
 */
export function approvePresidioMockQuote(quoteId: string, _notes?: string): PresidioQuoteEnrichment | undefined {
  return getPresidioQuoteEnrichment(quoteId);
}

/**
 * DEMO: map Account Manager display name → email (customer integration in production).
 * Used only for the quote-rejection AM notification sink (FR21.3.6).
 */
const PRESIDIO_MOCK_ACCOUNT_MANAGER_EMAILS: Record<string, string> = {
  'Eric Mohr': 'eric.mohr@presidio.com',
  'Vincent F Trama': 'vincent.trama@presidio.com',
  'Sam Rivera': 'sam.rivera@presidio.com',
  'Bob Foote': 'bob.foote@presidio.com',
};

/** DEMO: resolve AM email for a quote enrichment (PROVISIONAL). */
export function resolvePresidioMockAccountManagerEmail(
  accountManager?: string
): { accountManager: string; accountManagerEmail: string } | undefined {
  const name = (accountManager || '').trim();
  if (!name) {
    return undefined;
  }
  const mapped = PRESIDIO_MOCK_ACCOUNT_MANAGER_EMAILS[name];
  if (mapped) {
    return { accountManager: name, accountManagerEmail: mapped };
  }
  // Fallback: slugify display name for inspectable demo addresses.
  const local = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '');
  return { accountManager: name, accountManagerEmail: `${local || 'account.manager'}@presidio.com` };
}

/**
 * DEMO: Reject inbound quote, post comments (+ optional attachment) to MyOrders,
 * and email the Account Manager (MVP 3.4.1 / FR13.7.7 / FR21.3.6).
 * PROVISIONAL until ICM/MyOrders + mail contracts confirmed.
 */
export function rejectPresidioMockQuote(
  quoteId: string,
  comment?: string,
  attachmentName?: string
): PresidioQuoteEnrichment | undefined {
  const current = getPresidioQuoteEnrichment(quoteId);
  if (!current || !isPresidioQuoteCustomerVisible(current)) {
    return current;
  }
  const trimmed = (comment || '').trim();
  if (!trimmed) {
    return current;
  }
  const fileName = (attachmentName || '').trim() || undefined;
  const rejectedAt = new Date().toISOString();
  const overlays = readOverlays();
  overlays[quoteId] = {
    ...overlays[quoteId],
    rejected: true,
    rejectionComment: trimmed,
    rejectionAttachmentName: fileName,
    rejectedAt,
  };
  writeOverlays(overlays);
  const am = resolvePresidioMockAccountManagerEmail(current.accountManager);
  appendPresidioMockMyOrdersRejection({
    quoteId,
    rejectionComment: trimmed,
    rejectedAt,
    accountManager: am?.accountManager,
    accountManagerEmail: am?.accountManagerEmail,
    attachmentName: fileName,
  });
  if (am) {
    appendPresidioMockQuoteRejectionAmEmail({
      quoteId,
      rejectionComment: trimmed,
      rejectedAt,
      accountManager: am.accountManager,
      accountManagerEmail: am.accountManagerEmail,
      attachmentName: fileName,
    });
  }
  return getPresidioQuoteEnrichment(quoteId);
}

function readMyOrdersRejections(): PresidioMockMyOrdersRejection[] {
  if (typeof sessionStorage === 'undefined') {
    return [];
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_MYORDERS_REJECTIONS_KEY);
    const parsed = raw ? (JSON.parse(raw) as PresidioMockMyOrdersRejection[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function appendPresidioMockMyOrdersRejection(entry: PresidioMockMyOrdersRejection): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const next = [...readMyOrdersRejections(), entry];
  sessionStorage.setItem(PRESIDIO_MOCK_MYORDERS_REJECTIONS_KEY, JSON.stringify(next));
}

/** DEMO: list MyOrders rejection payloads (newest last). */
export function listPresidioMockMyOrdersRejections(): PresidioMockMyOrdersRejection[] {
  return readMyOrdersRejections();
}

function readQuoteRejectionAmEmails(): PresidioMockQuoteRejectionAmEmail[] {
  if (typeof sessionStorage === 'undefined') {
    return [];
  }
  try {
    const raw = sessionStorage.getItem(PRESIDIO_MOCK_QUOTE_REJECTION_AM_EMAILS_KEY);
    const parsed = raw ? (JSON.parse(raw) as PresidioMockQuoteRejectionAmEmail[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function appendPresidioMockQuoteRejectionAmEmail(entry: PresidioMockQuoteRejectionAmEmail): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  const next = [...readQuoteRejectionAmEmails(), entry];
  sessionStorage.setItem(PRESIDIO_MOCK_QUOTE_REJECTION_AM_EMAILS_KEY, JSON.stringify(next));
}

/** DEMO: list Account Manager emails recorded for quote rejections (newest last). */
export function listPresidioMockQuoteRejectionAmEmails(): PresidioMockQuoteRejectionAmEmail[] {
  return readQuoteRejectionAmEmails();
}

export function clearPresidioMockQuoteApprovals(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(PRESIDIO_MOCK_QUOTE_OVERLAYS_KEY);
    sessionStorage.removeItem(PRESIDIO_MOCK_MYORDERS_REJECTIONS_KEY);
    sessionStorage.removeItem(PRESIDIO_MOCK_QUOTE_REJECTION_AM_EMAILS_KEY);
    clearPresidioActiveQuoteOnBasket();
  }
}

function attr(name: string, type: string, value: unknown): { name: string; type: string; value: unknown } {
  return { name, type, value };
}

/** Build ICM-style list link element from enrichment. */
export function buildPresidioQuoteListElement(enrichment: PresidioQuoteEnrichment): Record<string, unknown> {
  const status = resolvePresidioInboundQuoteStatus(enrichment);
  // Only portal Generate Quote sets submittedDate; do not invent it for all Submitted quotes.
  const submittedDate = enrichment.submittedDate;
  const attributes = [
    attr('number', 'String', enrichment.number),
    attr('name', 'String', enrichment.displayName),
    attr('lineItems', 'Integer', enrichment.itemCount),
    attr('creationDate', 'Date', enrichment.creationDate),
    attr('validFromDate', 'Date', enrichment.validFromDate),
    attr('validToDate', 'Date', enrichment.validToDate),
    attr('rejected', 'Boolean', !!enrichment.rejected),
    attr('poNumber', 'String', enrichment.poNumber || ''),
    attr('orderNumber', 'String', enrichment.orderNumber || ''),
    attr('invoiceNumber', 'String', enrichment.invoiceNumber || ''),
    attr('pricingRequestNumber', 'String', enrichment.pricingRequestNumber || ''),
    attr('contact', 'String', enrichment.contact || ''),
    attr('contractVehicle', 'String', enrichment.contractVehicle || ''),
    attr('affiliatedCompany', 'String', enrichment.affiliatedCompany || ''),
    attr('partNumbers', 'String', (enrichment.partNumbers || []).join(' ')),
    attr('pendingApproval', 'Boolean', false),
    attr('purchased', 'Boolean', status === 'Processing'),
    attr('inBasket', 'Boolean', isPresidioQuoteOnActiveBasket(enrichment.id)),
    attr('customerVisible', 'Boolean', enrichment.customerVisible),
    attr('quoteCategory', 'String', enrichment.quoteCategory),
    attr('inboundStatus', 'String', status),
    attr('total', 'Double', enrichment.totalValue ?? 0),
  ];
  if (submittedDate) {
    attributes.splice(6, 0, attr('submittedDate', 'Date', submittedDate));
  }
  return {
    type: 'Link',
    uri: `inSPIRED-inTRONICS_Business-Site/-/customers/PRESIDIO-MOCK/users/jordan.hale@acme.com/quotes/${enrichment.id}`,
    title: enrichment.id,
    itemId: enrichment.id,
    attributes,
  };
}

/**
 * Filter quote list envelope to customer-visible quotes only (3.1.5).
 * When catalog has entries, rebuild list from catalog+overlays so demo stays consistent.
 */
export function applyPresidioQuoteListVisibility(body: unknown): unknown {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const visible = listVisiblePresidioQuoteIds().map(id => getPresidioQuoteEnrichment(id)!);
  return {
    ...(body as object),
    elements: visible.map(buildPresidioQuoteListElement),
  };
}

/** Build a mapper-safe quote detail body from catalog enrichment (demo source of truth). */
export function buildPresidioQuoteDetailFromEnrichment(
  enrichment: PresidioQuoteEnrichment
): Record<string, unknown> {
  const currency = 'USD';
  const items = (enrichment.lineMeta || []).map((meta, index) => {
    const quantity = meta.quantity ?? 1;
    const unitPrice =
      meta.unitPrice ??
      Math.round((enrichment.totalValue || 0) / Math.max(enrichment.itemCount || 1, 1));
    const ext = unitPrice * quantity;
    return {
      type: 'QuoteLineItem',
      id: `q-${meta.productSKU}-${index + 1}`,
      productSKU: meta.productSKU,
      quantity: { name: 'Quantity', type: 'Quantity', value: quantity, unit: 'N/A' },
      originSinglePrice: { type: 'Money', value: unitPrice, currency, currencyMnemonic: currency },
      singlePrice: { type: 'Money', value: unitPrice, currency, currencyMnemonic: currency },
      originTotalPrice: { type: 'Money', value: ext, currency, currencyMnemonic: currency },
      totalPrice: { type: 'Money', value: ext, currency, currencyMnemonic: currency },
      lineNumber: meta.lineNumber,
      manufacturer: meta.manufacturer,
      manufacturerPartNumber: meta.manufacturerPartNumber || meta.productSKU,
      unspsc: meta.unspsc,
      lineType: meta.lineType,
      name: meta.name,
      groupKey: meta.groupKey,
      groupParent: meta.groupParent,
      recurringStart: meta.recurringStart,
      recurringEnd: meta.recurringEnd,
      months: meta.months,
    };
  });
  const total = enrichment.totalValue ?? items.reduce((sum, i) => sum + Number(i.totalPrice.value), 0);
  return {
    type: 'Quote',
    id: enrichment.id,
    number: enrichment.number,
    displayName: enrichment.displayName,
    creationDate: enrichment.creationDate,
    validFromDate: enrichment.validFromDate,
    validToDate: enrichment.validToDate,
    rejected: !!enrichment.rejected,
    sellerComment: enrichment.sellerComment || '',
    total: { type: 'Money', value: total, currency, currencyMnemonic: currency },
    originTotal: { type: 'Money', value: total, currency, currencyMnemonic: currency },
    items,
    affiliatedCompany: enrichment.affiliatedCompany,
  };
}

/**
 * Merge enrichment onto quote detail GET.
 * Returns null when quote is not customer-visible (caller should 404).
 */
export function applyPresidioQuoteDetailOverlay(quoteId: string, body: unknown): unknown | null {
  const enrichment = getPresidioQuoteEnrichment(quoteId);
  if (!enrichment) {
    return body;
  }
  if (!isPresidioQuoteCustomerVisible(enrichment)) {
    return null;
  }
  const base =
    body && typeof body === 'object'
      ? (body as Record<string, unknown>)
      : buildPresidioQuoteDetailFromEnrichment(enrichment);
  const status = resolvePresidioInboundQuoteStatus(enrichment);
  const clone = { ...base };
  if (!Array.isArray(clone.items) || !(clone.items as unknown[]).length) {
    clone.items = buildPresidioQuoteDetailFromEnrichment(enrichment).items;
  }
  clone.rejected = !!enrichment.rejected;
  clone.rejectionComment = enrichment.rejectionComment || undefined;
  clone.sellerComment = enrichment.sellerComment || clone.sellerComment;
  clone.displayName = enrichment.displayName;
  clone.number = enrichment.number;
  clone.poNumber = enrichment.poNumber;
  clone.orderNumber = enrichment.orderNumber;
  clone.invoiceNumber = enrichment.invoiceNumber;
  clone.pricingRequestNumber = enrichment.pricingRequestNumber;
  clone.contact = enrichment.contact;
  clone.contractVehicle = enrichment.contractVehicle;
  clone.affiliatedCompany = enrichment.affiliatedCompany;
  clone.pendingApproval = false;
  clone.purchased = status === 'Processing';
  clone.submittedDate = enrichment.submittedDate;
  clone.inBasket = isPresidioQuoteOnActiveBasket(enrichment.id);
  clone.salesNotes = enrichment.salesNotes || enrichment.sellerComment;
  clone.accountManager = enrichment.accountManager;
  clone.insideSalesRep = enrichment.insideSalesRep;
  clone.addresses = enrichment.addresses || [];
  clone.attachments = enrichment.attachments || [];
  clone.approvalSteps = enrichment.approvalSteps || [];
  clone.validFromDate = enrichment.validFromDate;
  clone.validToDate = enrichment.validToDate;
  clone.inboundStatus = status;
  if (enrichment.totalValue != null) {
    const currency =
      (clone.total as { currency?: string } | undefined)?.currency ||
      (clone.total as { currencyMnemonic?: string } | undefined)?.currencyMnemonic ||
      'USD';
    clone.subtotal = { type: 'Money', value: enrichment.totalValue, currency, currencyMnemonic: currency };
  }

  if (Array.isArray(clone.items) && enrichment.lineMeta?.length) {
    clone.items = (clone.items as Array<Record<string, unknown>>).map((item, index) => {
      const meta =
        enrichment.lineMeta!.find(m => m.productSKU === item.productSKU) || enrichment.lineMeta![index];
      if (!meta) {
        return item;
      }
      return {
        ...item,
        lineNumber: meta.lineNumber,
        manufacturer: meta.manufacturer,
        manufacturerPartNumber: meta.manufacturerPartNumber || meta.productSKU,
        unspsc: meta.unspsc,
        lineType: meta.lineType,
        recurringStart: meta.recurringStart,
        recurringEnd: meta.recurringEnd,
        months: meta.months,
        name: meta.name,
        groupKey: meta.groupKey,
        groupParent: meta.groupParent,
      };
    });
  }
  return clone;
}
